import type { ActionType } from '@/types'

/**
 * Converts raw JSONB results from resolved party actions into human-readable summary strings.
 * Each action type has its own formatting logic based on the results structure
 * produced by the tick processor.
 */

export function formatActionResult(
  actionType: ActionType,
  results: Record<string, unknown> | null
): string {
  if (!results) return 'Pending resolution'

  // If the tick processor wrote a summary, prefer it
  if (typeof results.summary === 'string') {
    return results.summary
  }

  switch (actionType) {
    case 'campaign':
      return formatCampaignResult(results)
    case 'fundraise':
      return formatFundraiseResult(results)
    case 'attack':
      return formatAttackResult(results)
    case 'pr':
      return formatPRResult(results)
    case 'internal':
      return formatInternalResult(results)
    case 'organize':
      return formatOrganizeResult(results)
    case 'poll':
      return formatPollResult(results)
    case 'recruit':
      return formatRecruitResult(results)
    default:
      return 'Action resolved'
  }
}

function formatCampaignResult(results: Record<string, unknown>): string {
  const changes = results.recognitionChanges as
    | Array<{ districtName?: string; oldValue: number; newValue: number }>
    | undefined
  if (changes && changes.length > 0) {
    const first = changes[0]
    const delta = +(first.newValue - first.oldValue).toFixed(1)
    const sign = delta >= 0 ? '+' : ''
    if (changes.length === 1 && first.districtName) {
      return `Recognition ${sign}${delta} in ${first.districtName}`
    }
    return `Recognition changed across ${changes.length} districts`
  }
  return 'Campaign completed'
}

function formatFundraiseResult(results: Record<string, unknown>): string {
  const money = results.moneyRaised as number | undefined
  const bloc = results.donorBloc as string | undefined
  if (money !== undefined) {
    const formatted = money.toLocaleString()
    const blocLabel = bloc ? ` from ${formatSubtype(bloc)} donors` : ''
    return `Raised $${formatted}${blocLabel}`
  }
  return 'Fundraising completed'
}

function formatAttackResult(results: Record<string, unknown>): string {
  const subtype = results.subtype as string | undefined

  if (subtype === 'opposition_research') {
    const discovered = results.scandalDiscovered as boolean | undefined
    if (discovered) {
      const scandal = results.scandal as { type?: string; targetPartyName?: string } | undefined
      if (scandal) {
        return `Discovered ${scandal.type} scandal on ${scandal.targetPartyName}`
      }
      return 'Scandal discovered'
    }
    return 'No scandal discovered'
  }

  // negative_ads
  if (results.backfired) {
    return 'Attack backfired! Prestige lost'
  }
  const targetName = results.targetPartyName as string | undefined
  const reduction = results.totalRecognitionReduction as number | undefined
  if (targetName && reduction !== undefined) {
    return `${targetName} recognition -${Math.abs(reduction).toFixed(1)}`
  }
  return 'Attack completed'
}

function formatPRResult(results: Record<string, unknown>): string {
  const subtype = results.subtype as string | undefined

  if (subtype === 'damage_control') {
    if (results.scandalResolved) return 'Scandal resolved successfully'
    return 'Damage control applied'
  }

  if (subtype === 'highlight_success') {
    const mc = results.momentumChange as { change?: number } | undefined
    if (mc?.change !== undefined) {
      const sign = mc.change >= 0 ? '+' : ''
      return `Momentum ${sign}${mc.change.toFixed(1)}`
    }
    return 'Success highlighted'
  }

  // general_messaging
  const mc = results.momentumChange as { change?: number } | undefined
  const rc = results.recognitionChanges as Array<{ districtName?: string }> | undefined
  const parts: string[] = []
  if (mc?.change !== undefined) {
    const sign = mc.change >= 0 ? '+' : ''
    parts.push(`Momentum ${sign}${mc.change.toFixed(1)}`)
  }
  if (rc && rc.length > 0) {
    parts.push(`recognition in ${rc.length} district${rc.length > 1 ? 's' : ''}`)
  }
  return parts.length > 0 ? parts.join(', ') : 'PR action completed'
}

function formatInternalResult(results: Record<string, unknown>): string {
  const subtype = results.subtype as string | undefined

  if (subtype === 'loyalty_maintenance') {
    const count = results.candidatesAffected as number | undefined
    const avg = results.averageLoyaltyGain as number | undefined
    if (count !== undefined && avg !== undefined) {
      return `Loyalty +${avg.toFixed(1)} avg for ${count} candidate${count > 1 ? 's' : ''}`
    }
    return 'Loyalty maintenance completed'
  }

  if (subtype === 'address_grievance') {
    const name = results.candidateName as string | undefined
    const lc = results.loyaltyChange as { change?: number } | undefined
    if (name && lc?.change !== undefined) {
      return `${name} loyalty +${lc.change.toFixed(1)}`
    }
    return 'Grievance addressed'
  }

  if (subtype === 'promote_candidate') {
    const name = results.candidateName as string | undefined
    const rc = results.roleChange as { newRole?: string } | undefined
    if (name && rc?.newRole) {
      return `${name} promoted to ${formatSubtype(rc.newRole)}`
    }
    return 'Candidate promoted'
  }

  return 'Internal action completed'
}

function formatOrganizeResult(results: Record<string, unknown>): string {
  const districtName = results.districtName as string | undefined
  const oc = results.organizationChange as { change?: number } | undefined
  if (districtName && oc?.change !== undefined) {
    const sign = oc.change >= 0 ? '+' : ''
    return `Organization ${sign}${oc.change.toFixed(1)} in ${districtName}`
  }
  return 'Organization completed'
}

function formatPollResult(results: Record<string, unknown>): string {
  if (results.warning) return 'Results available'
  return 'Poll results available'
}

function formatRecruitResult(results: Record<string, unknown>): string {
  if (results.warning) return 'Recruited instantly'
  return 'Recruitment completed'
}

/**
 * Format a parameters JSONB object into a readable key-value list.
 */
export function formatParameters(
  parameters: Record<string, unknown> | null
): Array<{ label: string; value: string }> {
  if (!parameters) return []

  const entries: Array<{ label: string; value: string }> = []

  for (const [key, value] of Object.entries(parameters)) {
    if (value === null || value === undefined) continue

    // Skip large nested objects in summary (shown raw in expanded view)
    if (typeof value === 'object' && !Array.isArray(value)) {
      entries.push({ label: formatSubtype(key), value: JSON.stringify(value) })
      continue
    }

    if (Array.isArray(value)) {
      entries.push({ label: formatSubtype(key), value: `${value.length} items` })
      continue
    }

    entries.push({
      label: formatSubtype(key),
      value: String(value),
    })
  }

  return entries
}

/**
 * Format a results JSONB object into a readable key-value list for the expanded detail view.
 */
export function formatResultsDetailed(
  actionType: ActionType,
  results: Record<string, unknown> | null
): Array<{ label: string; value: string }> {
  if (!results) return []

  const entries: Array<{ label: string; value: string }> = []

  // Show key metrics based on action type
  switch (actionType) {
    case 'campaign': {
      const rc = results.recognitionChanges as
        | Array<{ districtName?: string; oldValue: number; newValue: number }>
        | undefined
      if (rc) {
        for (const change of rc) {
          const delta = +(change.newValue - change.oldValue).toFixed(1)
          const sign = delta >= 0 ? '+' : ''
          entries.push({
            label: change.districtName ?? 'District',
            value: `${change.oldValue.toFixed(1)} → ${change.newValue.toFixed(1)} (${sign}${delta})`,
          })
        }
      }
      if (results.isNational) entries.push({ label: 'Scope', value: 'National' })
      break
    }
    case 'fundraise': {
      if (results.moneyRaised !== undefined)
        entries.push({ label: 'Money raised', value: `$${(results.moneyRaised as number).toLocaleString()}` })
      if (results.donorBloc)
        entries.push({ label: 'Donor bloc', value: formatSubtype(results.donorBloc as string) })
      if (results.alignment !== undefined)
        entries.push({ label: 'Alignment', value: `${results.alignment}%` })
      const bonuses = results.bonuses as { prestige?: number; momentum?: number } | undefined
      if (bonuses?.prestige) entries.push({ label: 'Prestige bonus', value: `${bonuses.prestige}%` })
      if (bonuses?.momentum) entries.push({ label: 'Momentum bonus', value: `${bonuses.momentum}%` })
      break
    }
    case 'attack': {
      if (results.backfired !== undefined)
        entries.push({ label: 'Backfired', value: results.backfired ? 'Yes' : 'No' })
      if (results.targetPartyName)
        entries.push({ label: 'Target', value: results.targetPartyName as string })
      if (results.totalRecognitionReduction !== undefined)
        entries.push({ label: 'Recognition reduction', value: (results.totalRecognitionReduction as number).toFixed(1) })
      const scandal = results.scandal as { type?: string; severity?: string; prestigeHit?: number } | undefined
      if (scandal) {
        entries.push({ label: 'Scandal type', value: formatSubtype(scandal.type ?? '') })
        if (scandal.severity) entries.push({ label: 'Severity', value: formatSubtype(scandal.severity) })
        if (scandal.prestigeHit) entries.push({ label: 'Prestige impact', value: String(scandal.prestigeHit) })
      }
      break
    }
    case 'pr': {
      const mc = results.momentumChange as { oldValue?: number; newValue?: number; change?: number } | undefined
      if (mc) {
        if (mc.oldValue !== undefined && mc.newValue !== undefined)
          entries.push({ label: 'Momentum', value: `${mc.oldValue.toFixed(1)} → ${mc.newValue.toFixed(1)}` })
      }
      if (results.scandalResolved !== undefined)
        entries.push({ label: 'Scandal resolved', value: results.scandalResolved ? 'Yes' : 'No' })
      if (results.prestigeRecovery !== undefined)
        entries.push({ label: 'Prestige recovered', value: String(results.prestigeRecovery) })
      break
    }
    case 'internal': {
      const lcs = results.loyaltyChanges as
        | Array<{ candidateName: string; change: number }>
        | undefined
      if (lcs) {
        for (const lc of lcs) {
          entries.push({ label: lc.candidateName, value: `Loyalty +${lc.change.toFixed(1)}` })
        }
      }
      const lc = results.loyaltyChange as { oldLoyalty?: number; newLoyalty?: number; change?: number } | undefined
      if (lc && lc.oldLoyalty !== undefined) {
        entries.push({ label: 'Loyalty', value: `${lc.oldLoyalty.toFixed(1)} → ${lc.newLoyalty?.toFixed(1)}` })
      }
      const rc = results.roleChange as { oldRole?: string; newRole?: string } | undefined
      if (rc) {
        entries.push({ label: 'Role change', value: `${formatSubtype(rc.oldRole ?? '')} → ${formatSubtype(rc.newRole ?? '')}` })
      }
      break
    }
    case 'organize': {
      const oc = results.organizationChange as { oldValue?: number; newValue?: number; change?: number } | undefined
      if (oc && oc.oldValue !== undefined) {
        entries.push({ label: 'Organization', value: `${oc.oldValue.toFixed(1)} → ${oc.newValue?.toFixed(1)}` })
      }
      if (results.districtName)
        entries.push({ label: 'District', value: results.districtName as string })
      break
    }
    default:
      break
  }

  return entries
}

function formatSubtype(subtype: string): string {
  return subtype
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
